import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Shield } from "lucide-react";
import SEO from "@/components/common/SEO";
import { PLANES } from "@/data/staticContent";

export default function Planes() {
    return (
        <>
            <SEO
                title="Planes · Tres formas de destruirse"
                description="Catálogo de planes irónicos: Inicio, Profesional y Sin Retorno. Tres niveles de servicio. Todos llevan al mismo destino, solo cambia la velocidad."
                path="/planes"
                keywords="adicción, planes, pricing satírico, recaída, dependencia, recuperación"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    name: "Planes Autodestrucción S.A.",
                    itemListElement: PLANES.map((p, i) => ({
                        "@type": "ListItem",
                        position: i + 1,
                        name: `Plan ${p.name}`,
                        description: p.tagline,
                    })),
                }}
            />

            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <p className="eyebrow mb-6">Pricing · Sección 02</p>
                    <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mb-6">
                        Elige tu plan{" "}
                        <span className="text-[var(--danger)]">ideal</span>
                    </h1>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed">
                        Tres niveles de servicio. Todos llevan al mismo
                        destino, solo cambia la velocidad. Sin devoluciones,
                        sin horario de atención, sin soporte emocional.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24" data-testid="planes-list">
                <div className="container-x">
                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {PLANES.map((plan) => (
                            <article
                                key={plan.slug}
                                className={`relative bg-[#1a1a1a] border ${plan.featured ? "border-[var(--danger)] lg:scale-105" : "border-[var(--line)]"} p-8 lg:p-10`}
                                data-testid={`plan-${plan.slug}`}
                                id={plan.slug}
                            >
                                {plan.featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 hazard-stripes-thin px-4 py-1.5">
                                        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black">
                                            Más recomendado
                                        </span>
                                    </div>
                                )}
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2">
                                    {plan.code}
                                </p>
                                <h2 className="font-display text-5xl mb-4 text-[var(--paper-2)]">
                                    {plan.name}
                                </h2>
                                <p className="font-mono text-xs text-[var(--ink-muted)] mb-6 min-h-[3em]">
                                    {plan.tagline}
                                </p>
                                <div className="mb-8 pb-6 border-b border-dashed border-[var(--ink-dim)]">
                                    <p className="font-display text-2xl text-[var(--paper-2)] mb-1">
                                        {plan.priceLabel}
                                    </p>
                                    <p className="font-hand text-[var(--danger)] text-2xl -rotate-1">
                                        {plan.priceSub}
                                    </p>
                                </div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-4">
                                    Incluye:
                                </p>
                                <ul className="space-y-3 mb-10">
                                    {plan.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="font-mono text-sm text-[var(--paper)] flex gap-3"
                                        >
                                            <AlertTriangle className="w-4 h-4 text-[var(--danger)] flex-shrink-0 mt-0.5" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={plan.ctaPath}
                                    className={`flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] py-4 border-2 transition-colors ${plan.featured ? "border-[var(--danger)] bg-[var(--danger)] text-[var(--paper-2)] hover:bg-transparent hover:text-[var(--danger)]" : "border-[var(--paper-2)] text-[var(--paper-2)] hover:bg-[var(--paper-2)] hover:text-black"}`}
                                    data-testid={`plan-cta-${plan.slug}`}
                                >
                                    {plan.cta}
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Disclaimer */}
            <section className="py-16 bg-[#0d0d0d]">
                <div className="container-x">
                    <div className="border-2 border-[var(--warning)] p-8 md:p-12 max-w-4xl mx-auto">
                        <div className="flex flex-col md:flex-row items-start gap-6">
                            <Shield className="w-12 h-12 text-[var(--warning)] flex-shrink-0" />
                            <div>
                                <p className="eyebrow mb-3" style={{ color: "var(--warning)" }}>
                                    <span style={{ background: "var(--warning)" }} className="inline-block w-7 h-px"></span>
                                    Aviso obligatorio
                                </p>
                                <h3 className="font-display text-2xl md:text-3xl mb-4">
                                    Ningún plan es real.
                                </h3>
                                <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                    Estos "planes" son una sátira corporativa
                                    del consumo. Si te reflejaste en alguno,
                                    no es casualidad: es una invitación a
                                    revisarte. La adicción no se vende como
                                    servicio, se trata con ayuda profesional.{" "}
                                    <Link
                                        to="/salida"
                                        className="text-[var(--danger)] underline font-bold"
                                    >
                                        Mira la salida de emergencia
                                    </Link>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
